/**
 * @description Component-FundingRate
 */
// import './index.less';

import { CHAIN_ID } from '@derivation-tech/context';
import React, { FC, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import { FAQ_LINKS } from '@/constants/links';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { IMarketPair } from '@/features/market/types';
import classNames from 'classnames';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  currentPair: WrappedPair | undefined;
  chainId: CHAIN_ID | undefined;
  marketPairInfo: IMarketPair | undefined;
}
const FundingRate: FC<IPropTypes> = function ({ currentPair, marketPairInfo, className }) {
  const { t } = useTranslation();
  // const dispatch = useAppDispatch();

  // const fundingRate = usePairLast1HFundingRate(chainId, currentPair?.id);
  // const onTooltipVisibleChange = useCallback(
  //   (open: boolean) => {
  //     if (open) {
  //       // if (chainId && currentPair) dispatch(fetchLast1HFundingRate({ chainId, pair: currentPair }));
  //     }
  //   },
  //   [chainId, currentPair, dispatch],
  // );

  const _1hPeriodsFunding = useMemo(() => {
    if (marketPairInfo?.periods1hFunding)
      return {
        long: WrappedBigNumber.from(marketPairInfo?.periods1hFunding.long),
        short: WrappedBigNumber.from(marketPairInfo?.periods1hFunding.short),
      };
    return currentPair?.fundingFeeRateForLongAndShort;
  }, [currentPair?.fundingFeeRateForLongAndShort, marketPairInfo?.periods1hFunding]);

  const _1hLastFunding = useMemo(() => {
    if (marketPairInfo?.last1hFunding)
      return {
        long: WrappedBigNumber.from(marketPairInfo?.last1hFunding.long),
        short: WrappedBigNumber.from(marketPairInfo?.last1hFunding.short),
      };
  }, [marketPairInfo?.last1hFunding]);

  // useEffect(() => {
  //   if (chainId && currentPair && currentPair.isPerpetual) {
  //     // dispatch(fetchLast1HFundingRate({ chainId, pair: currentPair }));
  //   }
  // }, [currentPair?.id]);

  const fundingRatePerHour = useMemo(() => {
    if (marketPairInfo?.fundingRatePerHour) return WrappedBigNumber.from(marketPairInfo?.fundingRatePerHour);
  }, [marketPairInfo?.fundingRatePerHour]);

  if (!currentPair?.isPerpetual) return null;
  return (
    <div className={classNames('syn-pair-info-right-item', className)}>
      <div className="syn-pair-info-right-item-title">
        <LeanMoreToolTip
          overlayInnerStyle={{
            width: 400,
            paddingTop: 12,
            paddingLeft: 16,
            paddingBottom: 16,
            paddingRight: 16,
          }}
          // onOpenChange={onTooltipVisibleChange}
          link={FAQ_LINKS.PREDICTED_FUNDING_RATE}
          placement="bottom"
          trigger={['hover', 'click']}
          title={
            <>
              <div className="syn-pair-info-right-funding">
                <div className="syn-pair-info-right-funding-title">
                  <Trans i18nKey={'tooltip.tradePage.fundingPreHour'} components={{ b: <b /> }} />
                </div>
                <div className="syn-pair-info-right-funding-rate">
                  <span>{fundingRatePerHour?.gt(0) ? t('common.longPay') : t('common.shortPay')}</span>
                  <b>
                    <EmptyDataWrap isLoading={!_1hPeriodsFunding}>
                      {fundingRatePerHour?.gt(0)
                        ? _1hPeriodsFunding?.long?.abs?.()?.formatPercentage?.({
                            colorShader: false,
                            decimals: 4,
                          })
                        : _1hPeriodsFunding?.short?.abs?.()?.formatPercentage?.({
                            colorShader: false,
                            decimals: 4,
                          })}
                    </EmptyDataWrap>
                  </b>
                  <span className="divider">/</span>
                  <span> {fundingRatePerHour?.gt(0) ? t('common.shortReceive') : t('common.longReceive')}</span>
                  <b>
                    <EmptyDataWrap isLoading={!_1hPeriodsFunding}>
                      {fundingRatePerHour?.gt(0)
                        ? _1hPeriodsFunding?.short?.abs?.()?.formatPercentage?.({
                            colorShader: false,
                            decimals: 4,
                          })
                        : _1hPeriodsFunding?.long?.abs?.()?.formatPercentage?.({
                            colorShader: false,
                            decimals: 4,
                          })}
                    </EmptyDataWrap>
                  </b>
                </div>
              </div>

              {_1hLastFunding && (
                <div className="syn-pair-info-right-funding" style={{ marginTop: 8 }}>
                  <div className="syn-pair-info-right-funding-title">
                    <Trans i18nKey={'tooltip.tradePage.fundingLastHour'} components={{ b: <b /> }} />
                  </div>
                  <div className="syn-pair-info-right-funding-rate">
                    <span>
                      {_1hLastFunding?.long && _1hLastFunding?.long?.lt?.(0)
                        ? t('common.longPay')
                        : t('common.shortPay')}
                    </span>
                    <b>
                      <EmptyDataWrap isLoading={!_1hLastFunding}>
                        {_1hLastFunding?.long.lt(0)
                          ? _1hLastFunding.long.abs().formatPercentage({
                              colorShader: false,
                              decimals: 4,
                            })
                          : _1hLastFunding?.short.abs().formatPercentage({
                              colorShader: false,
                              decimals: 4,
                            })}
                      </EmptyDataWrap>
                    </b>
                    <span className="divider">/</span>
                    <span>
                      {' '}
                      {_1hLastFunding?.long && _1hLastFunding?.long.lt?.(0)
                        ? t('common.shortReceive')
                        : t('common.longReceive')}
                    </span>
                    <b>
                      <EmptyDataWrap isLoading={!_1hLastFunding}>
                        {_1hLastFunding?.long.lt(0)
                          ? _1hLastFunding?.short.abs().formatPercentage({
                              colorShader: false,
                              decimals: 4,
                            })
                          : _1hLastFunding?.long.abs().formatPercentage({
                              colorShader: false,
                              decimals: 4,
                            })}
                      </EmptyDataWrap>
                    </b>
                  </div>
                </div>
              )}
              <div className="syn-pair-info-right-funding-line" />
              {t('tooltip.tradePage.fundingLearMore')}
            </>
          }>
          {t('common.fundingEst')}
        </LeanMoreToolTip>
      </div>
      <div className="syn-pair-info-right-item-value">
        <EmptyDataWrap isLoading={!currentPair?.fundingRatePerHour}>
          {_1hPeriodsFunding?.short?.formatPercentage({
            hundredfold: true,
            colorShader: false,
            decimals: 4,
          })}
        </EmptyDataWrap>
      </div>
    </div>
  );
};

export default FundingRate;
